import { memo } from "react";
import { Box, VStack, Text, Link, HStack, Flex } from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { FiGitBranch, FiGitCommit } from "react-icons/fi";

type StageStatus = "completed" | "in-progress" | "pending" | "failed";

const STAGE_STYLES: Record<StageStatus, { bg: string; borderColor: string }> = {
  completed: {
    bg: "green.50",
    borderColor: "green.300",
  },
  "in-progress": {
    bg: "blue.100",
    borderColor: "blue.300",
  },
  pending: {
    bg: "white",
    borderColor: "gray.200",
  },
  failed: {
    bg: "red.50",
    borderColor: "red.300",
  },
} as const;

const BuildStage = memo(
  ({
    stage,
    selectedBuild,
  }: {
    stage: BuildStageInterface & { status: StageStatus };
    selectedBuild?: BuildUpdate;
  }) => {
    return (
      <Box
        p={4}
        bg={STAGE_STYLES[stage.status].bg}
        borderRadius="md"
        border="1px solid"
        borderColor={STAGE_STYLES[stage.status].borderColor}
      >
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Text fontWeight="bold">{stage.name}</Text>
            {stage.name === "Build Creation" && stage.timestamp && (
              <Text fontSize="sm" color="gray.600">
                {new Date(stage.timestamp).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            )}
          </HStack>

          <Text
            fontSize="sm"
            color="gray.600"
            mt={stage.name === "Build Creation" ? 2 : 0}
          >
            {stage.info}
          </Text>

          {stage.name === "Bundle Storage" &&
            stage.additionalInfo?.checksum && (
              <Text fontSize="sm" color="gray.500">
                <Text as="span" fontWeight="semibold">
                  Checksum:
                </Text>{" "}
                {stage.additionalInfo.checksum}
              </Text>
            )}

          {stage.name === "Build Creation" && (
            <Flex alignItems="center" gap={3}>
              {selectedBuild?.additionalProps.branchName && (
                <Flex alignItems="center" gap={1}>
                  <Box w="18px" h="18px">
                    <FiGitBranch size={16} color="#637381" />
                  </Box>
                  <Text fontSize="sm" color="gray.500">
                    {selectedBuild.additionalProps.branchName}
                  </Text>
                </Flex>
              )}
              {selectedBuild?.additionalProps.lastCommitMessage && (
                <Flex alignItems="center" gap={1}>
                  <Box w="18px" h="18px">
                    <FiGitCommit size={16} color="#637381" />
                  </Box>
                  <Text fontSize="sm" color="gray.500">
                    {selectedBuild.additionalProps.lastCommitMessage}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}

          {stage.link && (
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" color="gray.500">
                <Text as="span" fontWeight="semibold">
                  {stage.name === "Build Creation" && "Build URL:"}
                  {stage.name === "Bundle Storage" && "Bundle URL:"}
                  {stage.name === "On-chain Transaction" && "Transaction URL:"}
                </Text>
              </Text>
              <Link
                href={stage.link}
                target="_blank"
                rel="noopener noreferrer"
                display="block"
                maxW="100%"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace={{ base: "normal", md: "nowrap" }}
                wordBreak={{ base: "break-all", md: "normal" }}
                fontSize={{ base: "xs", md: "sm" }}
                color="blue.500"
                _hover={{ color: "blue.600" }}
                textDecoration="underline"
              >
                {stage.description} <ExternalLinkIcon mx="2px" />
              </Link>
            </VStack>
          )}
        </VStack>
      </Box>
    );
  },
);

export default BuildStage;
